import { escape, op } from 'arquero'

/**
 * Convert an array to an object, indexing on values of field
 * @param {Array} records
 * @param {String} field
 */
export const indexBy = (records, field) =>
  records.reduce(
    (prev, record) => Object.assign(prev, { [record[field]]: record }),
    {}
  )

/**
 * Convert an array to an object, grouping items that have the same key
 * @param {Array} records
 * @param {String} groupField - field in record for grouping
 * @returns
 * {<key>: [records], ...}
 */
export const groupBy = (data, groupField) =>
  data.reduce((prev, d) => {
    const key = d[groupField]
    prev[key] = (prev[key] || []).concat([d])
    return prev
  }, {})

/**
 * Convert an array to an object, grouping items that have the same key
 * @param {Array} records
 * @param {Function} keyFunc - returns an array of keys
 *
 * @returns
 * {<key>: [records], ...}
 */
export const groupByMultipleKeys = (records, keyFunc) => {
  const grouped = {}
  records.forEach((record) => {
    keyFunc(record).forEach((key) => {
      grouped[key] = grouped[key] ? [...grouped[key], record] : [record]
    })
  })
  return grouped
}

/**
 * Flatten an array of arrays (2D) to an array (1D)
 * @param {Array} records
 */
export const flatten = (records) =>
  records.reduce((prev, record) => {
    prev.push(...record)
    return prev
  }, [])

/**
 * Create a sorter that operates on a field within an object
 * @param {String} sortField - field to sort by
 * @param {Boolean} ascending - if results should be in ascending order (default: true)
 * @returns sortFunc
 */
export const sortByField =
  (sortField, ascending = true) =>
  ({ [sortField]: a }, { [sortField]: b }) => {
    if (ascending) {
      return a < b ? -1 : 1
    }
    return a > b ? -1 : 1
  }

/**
 * Create a sorter that operates on a date field within an object; dates are
 * parsed befroe sorting
 * @param {String} sortField - field to sort by
 * @param {Boolean} ascending - if results should be in ascending order (default: true)
 * @returns sortFunc
 */
export const sortByDateField =
  (sortField, ascending = true) =>
  ({ [sortField]: a }, { [sortField]: b }) => {
    const dateA = new Date(a)
    const dateB = new Date(b)

    if (ascending) {
      return dateA < dateB ? -1 : 1
    }
    return dateA > dateB ? -1 : 1
  }

/** Calculate bin based on dividing [0,1] range into numBins.
 * Returns null if input is null.
 * @param{String} - field name in arquero table
 * @param{Array} - array of bin values (e.g., labels)
 * @param{String | Number} - missing value to fill in where null / undefined
 */
export const bin = (field, bins, missingValue = null) =>
  escape((d) => {
    if (d[field] === null || d[field] === undefined) {
      // have to provide a value or it gets reduced out
      return missingValue
    }
    const index = op.least(
      op.floor(op.bin(d[field], 0, 1, 1 / bins.length) * bins.length),
      bins.length - 1
    )
    return bins[index]
  })
